import { dayjs } from 'date';
import ExifReader from 'exifreader';
import { logger } from 'monitor';

export interface ExifMetadata {
  timestamp?: string;
  deviceName?: string;
  make?: string;
  model?: string;
  software?: string;
}

const EXIF_INCOMING_DATETIME_FORMAT = 'YYYY:MM:DD HH:mm:ss';
const EXIF_DATETIME_FORMAT_REGEX = /^\d{4}:\d{2}:\d{2} \d{2}:\d{2}:\d{2}$/;
const EXIF_DATETIME_WITH_TIMEZONE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:Z|[+-]\d{2}:\d{2})$/;
const POSTGRES_TIMESTAMP_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const getTagDescription = (tag: { description?: string } | undefined): string | undefined => {
  const description = tag?.description?.trim();
  return description ? description : undefined;
};

const getUploadUserAgent = (): string | undefined => {
  if (typeof navigator === 'undefined') {
    return undefined;
  }

  return navigator.userAgent || undefined;
};

const buildDeviceName = (make?: string, model?: string): string | undefined => {
  if (make && model) {
    return model.toLowerCase().startsWith(make.toLowerCase()) ? model : `${make} ${model}`;
  }

  return model;
};

export const convertToPostgresUTCTimestamp = (timestamp: string) => {
  if (EXIF_DATETIME_WITH_TIMEZONE_REGEX.test(timestamp)) {
    return timestamp.substring(0, 19);
  }

  if (EXIF_DATETIME_FORMAT_REGEX.test(timestamp)) {
    return dayjs(timestamp, EXIF_INCOMING_DATETIME_FORMAT).format(POSTGRES_TIMESTAMP_FORMAT);
  }

  return undefined;
};

export const buildEvidenceMetadataFromExif = (
  exifData: ExifMetadata,
  metadata: Record<string, unknown> | undefined
): Record<string, unknown> => {
  const uploadUserAgent = getUploadUserAgent();
  const deviceName = exifData.deviceName !== exifData.make ? exifData.deviceName : undefined;
  const deviceModelName = exifData.model ?? deviceName;

  return {
    ...metadata,
    ...(deviceName ? { device_name: deviceName } : {}),
    ...(exifData.make ? { device_make: exifData.make } : {}),
    ...(exifData.model ? { device_model: exifData.model } : {}),
    ...(deviceModelName ? { device_model_name: deviceModelName } : {}),
    ...(exifData.software ? { device_software: exifData.software } : {}),
    ...(exifData.timestamp !== undefined ? { taken_at: exifData.timestamp } : {}),
    ...(uploadUserAgent ? { upload_user_agent: uploadUserAgent } : {}),
  };
};

export const extractExifData = async (file: File): Promise<ExifMetadata> => {
  try {
    const arrayBuffer = await file.arrayBuffer();

    const tags = ExifReader.load(arrayBuffer);

    const make = getTagDescription(tags.Make);
    const model = getTagDescription(tags.Model);
    const software = getTagDescription(tags.Software);
    const metadata: ExifMetadata = {
      deviceName: buildDeviceName(make, model),
      make,
      model,
      software,
    };

    if (tags.DateTimeOriginal?.description) {
      metadata.timestamp = convertToPostgresUTCTimestamp(tags.DateTimeOriginal.description);
    } else {
      logger.warn('No DateTimeOriginal found in EXIF data', { tags });

      metadata.timestamp = dayjs().format(POSTGRES_TIMESTAMP_FORMAT);
    }

    return metadata;
  } catch (error) {
    logger.error('Error extracting EXIF data:', error);
    return {
      timestamp: dayjs().format(POSTGRES_TIMESTAMP_FORMAT),
    };
  }
};
